import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import PageLayout from '@/components/layout/PageLayout'
import LiveStreamPreview from '@/components/LiveStreamPreview'
import { apiService } from '@/service/api'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { useTranslation } from '@/i18n'

/**
 * Full-page viewer for a `live_stream` anchor document (Eventhouse / streamed
 * tags), opened from the Data Catalog or from a board card's "Open".
 *
 * A separate route from /timeseries on purpose: a live stream has no file and
 * no copied values, so the file-shaped viewers (datasheet / JSON timeseries)
 * don't apply — and keeping it separate means the existing timeseries viewer is
 * completely untouched.
 *
 * `?boardName=&docName=` are the same params the board card appends for every
 * other viewer. They are read here (rather than ignored) so opening a live
 * stream from a board keeps the board's name and a way back to it — otherwise
 * the board card sends context the destination silently drops.
 */
const LiveStream = () => {
  const { doc_id } = useParams<{ doc_id: string }>()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const navigateBack = useNavigateBack()
  const boardName = searchParams.get('boardName')
  const docNameParam = searchParams.get('docName')
  const [name, setName] = useState<string | null>(null)
  // Distinct from `name === null`: without it a failed getDocument is
  // indistinguishable from "still loading" and the title says "Loading..."
  // forever. The body below renders regardless — it does its own fetching.
  const [nameResolved, setNameResolved] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!doc_id) {
        setNameResolved(true)
        return
      }
      const doc = (await apiService
        .getDocument(doc_id)
        .catch(() => null)) as { name?: string } | null
      if (cancelled) return
      setName(doc?.name ?? null)
      setNameResolved(true)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [doc_id])

  // docName is the board card's own label, so it titles the page correctly
  // before the fetch resolves and after a failed one.
  const title =
    name ||
    docNameParam ||
    (nameResolved
      ? t('document_viewer:live_stream.title', { defaultValue: 'Live connection' })
      : t('document_viewer:common.loading', { defaultValue: 'Loading...' }))

  return (
    <PageLayout title={title}>
      {boardName && (
        <button
          type="button"
          onClick={navigateBack}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground
                     hover:text-foreground transition-colors"
        >
          <ChevronLeft className="w-4 h-4 shrink-0" />
          <span className="truncate max-w-[18rem]">{boardName}</span>
        </button>
      )}
      <div className="border rounded-lg h-[calc(100vh-12rem)]">
        {doc_id && <LiveStreamPreview docId={doc_id} />}
      </div>
    </PageLayout>
  )
}

export default LiveStream
